import { createContext, useContext, useState } from "react";
import { Inviter } from "sip.js";
import {
  IProviderContext,
  RegisterStatus,
  CONNECT_STATUS,
  SessionDirection,
  Timer,
} from "./type";

export const ProviderContext = createContext<IProviderContext>({
  sessionManager: null,
  connectAndRegister: () => {},
  connectStatus: CONNECT_STATUS.WAIT_REQUEST_CONNECT,
  registerStatus: RegisterStatus.UNREGISTERED,
  sessions: {},
  sessionTimer: {},
  isCallAnswered: false,
});

/**
 * Provides direct access to SIPProvider context, e.g. to get statuses, sessions, or session manager.
 */
export const useSIPProvider = () => {
  return useContext(ProviderContext);
};

/**
 * Hook for controlling a single call session: answer, hangup, hold, mute, etc.
 */
export const useSessionCall = (sessionId: string) => {
  const { sessions, sessionManager, sessionTimer } = useSIPProvider();

  const session = sessions[sessionId];

  // Local state for whether we’re currently muted/held, derived from the SessionManager’s state
  const [isMuted, setIsMuted] = useState<boolean>(
    sessionManager?.isMuted(session) || false
  );
  const [isHeld, setIsHeld] = useState<boolean>(
    sessionManager?.isHeld(session) || false
  );

  // If the sessionId is invalid or we don't have that session, return null.
  if (!sessionId || !sessions[sessionId]) {
    return null;
  }

  // Determine if this session is inbound or outbound
  const direction =
    session instanceof Inviter
      ? SessionDirection.OUTGOING
      : SessionDirection.INCOMING;

  // Pull the call timers (createdAt, answeredAt, etc.)
  const timer: Timer | undefined = sessionTimer[sessionId];

  return {
    /** Outgoing or incoming call */
    direction,

    /** The raw SIP.js session object */
    session,

    /** Various timing info about the call */
    timer,

    /** Place the call on hold */
    hold: () => {
      sessionManager?.hold(session);
      setIsHeld(true);
    },

    /** Unhold the call */
    unhold: () => {
      sessionManager?.unhold(session);
      setIsHeld(false);
    },
    isHeld,

    /** Mute the call */
    mute: () => {
      sessionManager?.mute(session);
      setIsMuted(true);
    },

    /** Unmute the call */
    unmute: () => {
      sessionManager?.unmute(session);
      setIsMuted(false);
    },
    isMuted,

    /** Answer an incoming call */
    answer: () => sessionManager?.answer(session),

    /** Decline an incoming call */
    decline: () => sessionManager?.decline(session),

    /** Hang up/Terminate the call */
    hangup: () => {
      sessionManager?.hangup(session);
      sessionManager?.unregister();
      sessionManager?.disconnect();
    },
  };
};